import { useState, useRef, useEffect } from 'react';
import { formatCurrency } from '@atc/string-fns';
import { batch } from 'react-redux';
import clsx from 'clsx';
import { InfoIcon } from 'reaxl-molecules';
import { Text, ModalTrigger, Button, LinkIcon, LoadingSpinner, Row, Col } from 'reaxl';
import { TaxesFeesBreakdownForm, TaxesFeesModal } from 'reaxl-wallet';
import _isEmpty from 'lodash/isEmpty';
import { renderTaxesFeesBreakdown } from './renderTaxesFeesBreakdown.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
const renderOfferBreakdownModalContent = (isLoading, hasError, paymentErrorState, total, includeTaxesAndFees) => {
  if (isLoading) {
    return /*#__PURE__*/jsx("div", {
      className: "text-right",
      children: /*#__PURE__*/jsx(LoadingSpinner, {
        className: "display-inline-block margin-0"
      })
    });
  }
  return /*#__PURE__*/jsx(Row, {
    className: "flex-nowrap",
    children: /*#__PURE__*/jsx(Col, {
      xs: 5,
      md: 4,
      className: "flex-shrink-0",
      children: !(hasError || paymentErrorState) && !!total && total !== 0 && includeTaxesAndFees ? /*#__PURE__*/jsx(Text, {
        size: 300,
        componentClass: "div",
        className: "text-nowrap text-right",
        color: "subdued",
        children: (!!total || total === 0) && formatCurrency(Math.floor(total))
      }) : /*#__PURE__*/jsx(Text, {
        size: 300,
        componentClass: "div",
        className: "text-nowrap text-right",
        color: "subdued",
        children: "Not included"
      })
    })
  });
};

/**
 * Renders the standard taxes and fees content
 * @param {number} total - Total taxes and fees amount
 * @param {number} fontSize - Font size for text
 * @returns {React.ReactElement} Standard taxes and fees content
 */
const renderStandardTaxesFeesContent = (total, fontSize) => /*#__PURE__*/jsx(Row, {
  children: /*#__PURE__*/jsx(Col, {
    xs: 5,
    md: 4,
    className: "flex-shrink-0",
    children: total !== 0 ? /*#__PURE__*/jsxs(Text, {
      size: fontSize,
      componentClass: "div",
      className: "text-nowrap text-right",
      color: "subdued",
      children: ["+ ", formatCurrency(Math.floor(total))]
    }) : /*#__PURE__*/jsx(Text, {
      size: fontSize,
      componentClass: "div",
      className: "text-nowrap text-right",
      color: "subdued",
      children: "Check with dealer"
    })
  })
});

/**
 * Component to display taxes and fees details based on various conditions and UI contexts.
 * Handles different rendering modes including offer breakdown modal, snapshot, and default views.
 *
 * @component
 * @param {Object} props - The component props
 * @param {boolean} [props.includeTaxesAndFees] - Whether taxes and fees are included in calculation
 * @param {boolean} [props.isNationalRates] - Whether national rates are being used
 * @param {boolean} [props.isSnapshot] - Whether component is being used in snapshot context
 * @param {Function} [props.taxModalTrigger] - Function that renders the modal trigger component
 * @param {number} [props.totalTaxesFees] - Total taxes and fees amount
 * @param {number} [props.fontSize] - Font size to use for text elements
 * @param {boolean} [props.showInOfferBreakdownModal=false] - Whether rendering in offer breakdown modal
 * @param {boolean|Object} [props.isLoading=false] - Whether taxes and fees are currently loading (can be boolean or loading state object)
 * @param {boolean} [props.hasRequestedTaxesFees=false] - Whether user has previously requested taxes and fees
 * @param {string} [props.activeTab] - Currently active tab for conditional rendering
 * @param {boolean} [props.isUds] - Whether UDS redesign rollout is enabled
 * @param {Object} [props.taxesFees={}] - Taxes and fees object for the current tab
 * @param {boolean} [props.paymentErrorState=false] - Whether there's an error in payment calculation
 * @param {boolean} [props.hasError=false] - Whether there's an error in the component
 * @returns {React.ReactElement|null} The taxes and fees detail component or null based on conditions
 */
function TaxesAndFeesDetail(_ref) {
  let {
    includeTaxesAndFees,
    isNationalRates,
    isSnapshot,
    taxModalTrigger,
    totalTaxesFees,
    fontSize,
    showInOfferBreakdownModal = false,
    isLoading = false,
    hasRequestedTaxesFees = false,
    isUds = false,
    taxesFees = {},
    paymentErrorState = false,
    hasError = false
  } = _ref;
  if (isNationalRates || isSnapshot && !includeTaxesAndFees) {
    return /*#__PURE__*/jsx(Text, {
      componentClass: "div",
      className: "text-overflow",
      color: "subdued",
      children: "Not Available"
    });
  }
  if (showInOfferBreakdownModal) {
    return renderOfferBreakdownModalContent(isLoading, hasError, paymentErrorState, taxesFees?.totalTaxesFees || totalTaxesFees, includeTaxesAndFees);
  }
  if (includeTaxesAndFees) {
    return renderStandardTaxesFeesContent(totalTaxesFees, fontSize);
  }
  if (!showInOfferBreakdownModal && !(hasRequestedTaxesFees && isUds)) {
    return /*#__PURE__*/jsx("div", {
      className: "text-overflow",
      children: taxModalTrigger(false)
    });
  }
}

/**
 *  Component renders the Taxes and Fees CTA and modal trigger
 *
 */
function TaxesAndFeesCTA(_ref2) {
  let {
    showSeeDetails,
    handleTaxesFeesModalOpen,
    isWithRedesign
  } = _ref2;
  const ctaConfig = {
    showSeeDetails: {
      Component: Button,
      props: {
        bsStyle: 'text',
        onClick: handleTaxesFeesModalOpen
      },
      text: 'See Details'
    },
    isWithRedesign: {
      Component: LinkIcon,
      props: {
        href: undefined,
        glyph: null,
        undecorated: true
      },
      text: 'Get Taxes & Fees'
    },
    default: {
      Component: Button,
      props: {
        bsStyle: 'default',
        bsSize: 'small'
      },
      text: 'Get Taxes & Fees'
    }
  };
  let configKey = 'default';
  if (showSeeDetails) {
    configKey = 'showSeeDetails';
  } else if (isWithRedesign) {
    configKey = 'isWithRedesign';
  }
  const {
    Component,
    props,
    text
  } = ctaConfig[configKey];
  return /*#__PURE__*/jsx(Component, {
    ...props,
    children: text
  });
}

/**
 * Component that handles the display and management of taxes and fees in the deal calculator
 *
 * @component
 * @param {Object} props - The component props
 * @param {string} [props.activeTab='finance'] - Currently active tab (finance, lease, cash)
 * @param {string} props.brand - Brand identifier (e.g., 'kbb')
 * @param {Object} props.container - Container for Syndicated Calculator
 * @param {boolean|string} props.isEstimate - Determines if the 'Est." label is shown
 * @param {boolean} [props.enableTaxesAndFees=false] - Whether taxes and fees functionality is enabled
 * @param {Function} [props.fetchPaymentDetails=()=>{}] - Function to fetch payment details
 * @param {Function} [props.handleNDSClick=()=>{}] - Function to handle NDS click events
 * @param {Function} [props.handleNDSPencilling=()=>{}] - Function to handle NDS pencilling events
 * @param {boolean} [props.includeTaxesAndFees=false] - Whether taxes and fees are included in calculation
 * @param {boolean} props.isNationalRates - Whether national rates are being used
 * @param {string} props.ndsPencilingFeature - NDS penciling feature identifier
 * @param {Object} props.paymentCalculationDetails - Payment calculation details object
 * @param {Function} [props.setIncludeTaxesAndFees=()=>{}] - Function to update includeTaxesAndFees state
 * @param {Function} [props.setPaymentCalculationDetails=()=>{}] - Function to update payment calculation details
 * @param {Function} [props.setHasTaxesFeesModalOpen=()=>{}] - Function to set modal open state
 * @param {string} [props.tabName='finance'] - Name of the current tab
 * @param {Object} [props.taxesAndFeesObj={finance:{},lease:{},cash:{}}] - Object containing taxes and fees data
 * @param {number} props.totalTaxesFees - Total taxes and fees amount
 * @param {string} [props.uiContext='default'] - UI context ('default', 'offerBreakdown', 'offerBreakdownModal', 'snapshot' or 'withRedesign')
 * @param {Function} [props.updatePaymentPencils=()=>{}] - Function to update payment pencils
 * @param {Function} [props.updateUserPaymentInfo=()=>{}] - Function to update user payment information
 * @param {string} props.zip - ZIP code for tax calculation
 * @param {Function} [props.onNdsInitialClick=()=>{}] - Function to handle initial NDS click
 * @param {boolean} [props.isUds=false] - Whether UDS redesign rollout is enabled
 * @param {boolean|Object} [props.isLoading=false] - Whether taxes are currently being loaded (can be boolean or loading state object)
 * @param {Function} [props.setIsLoadingTaxes=()=>{}] - Function to set loading state for taxes
 * @param {boolean} [props.hasRequestedTaxesFees=false] - Whether user has previously requested taxes and fees
 * @param {boolean} [props.paymentErrorState=false] - Whether there's an error in payment calculation
 * @param {boolean} [props.hasTaxesFetchError=false] - Whether there's an error fetching taxes and fees
 * @param {Function} [props.fetchIncentivesDetails=()=>{}] - Function to fetch incentives details
 * @returns {React.ReactElement|null} The taxes and fees component or null if disabled
 */
function TaxesAndFees(_ref3) {
  let {
    activeTab = 'finance',
    brand,
    container,
    // For Syndicated Calc
    isEstimate,
    enableTaxesAndFees: showTaxesFees = false,
    fetchPaymentDetails = () => {},
    handleNDSClick = () => {},
    handleNDSPencilling = () => {},
    includeTaxesAndFees = false,
    // We can't move includeTaxesAndFees to configContext as it is UDS state variable but not a UDS config value.
    isNationalRates,
    ndsPencilingFeature,
    paymentCalculationDetails,
    setIncludeTaxesAndFees = () => {},
    setPaymentCalculationDetails = () => {},
    setHasTaxesFeesModalOpen = () => {},
    tabName = 'finance',
    taxesAndFeesObj = {
      finance: {},
      lease: {},
      cash: {}
    },
    totalTaxesFees,
    uiContext = 'default',
    updatePaymentPencils = () => {},
    updateUserPaymentInfo = () => {},
    zip,
    onNdsInitialClick = () => {},
    isUds = false,
    isLoading = false,
    setIsLoadingTaxes = () => {},
    hasRequestedTaxesFees = false,
    paymentErrorState = false,
    hasTaxesFetchError = false,
    fetchIncentivesDetails = () => {}
  } = _ref3;
  const [showTaxesFeesModal, setShowTaxesFeesModal] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [hasUpdatedZip, setHasUpdatedZip] = useState(false); // State to track if the ZIP code has been updated from each reference of the modal
  const userZip = useRef(zip);
  const prevFetchZip = useRef(zip); // Track the ZIP used for the last API call
  const taxesFees = taxesAndFeesObj[activeTab];
  const isLease = activeTab === 'lease';
  const showTotalTaxesOnly = !!taxesFees?.showTaxTotalOnly; // for Esntial UDS only

  const taxesFeesSectionProps = {
    isLoading,
    hasError: hasError || hasTaxesFetchError || paymentErrorState,
    taxesFees,
    showTotalTaxesOnly,
    isEstimate,
    isLease
  };
  const isOfferBreakdownModal = uiContext === 'offerBreakdownModal';
  const showInOfferBreakdownModal = isUds && isOfferBreakdownModal;
  const enableTaxesAndFees = isOfferBreakdownModal ? showTaxesFees : showTaxesFees && !paymentErrorState;
  useEffect(() => {
    if (isLoading && !_isEmpty(taxesFees)) {
      setIsLoadingTaxes(false);
      setHasError(false);
    } else if (_isEmpty(taxesFees)) {
      setIsLoadingTaxes(false);
      // OfferBreakdownModal relies on its own error state
      if (!showInOfferBreakdownModal) {
        setHasError(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxesFees]); // we need to check only for taxesAndFeesObj for state change.

  // for the OfferBreakdownModal only
  useEffect(() => {
    if (isOfferBreakdownModal && hasTaxesFetchError) {
      setIsLoadingTaxes(false);
      setHasError(true);
    } else if (isOfferBreakdownModal && hasUpdatedZip && userZip.current?.length === 5) {
      // Only run if the ZIP is different from what we last used for API call
      prevFetchZip.current = userZip.current.toString(); // Track the ZIP used for this API call
      fetchPaymentDetails(true, true, userZip.current.toString());
      setIncludeTaxesAndFees(true);
      setHasUpdatedZip(false); // Reset the flag after we use it
    } else if (isOfferBreakdownModal && !hasTaxesFetchError && !_isEmpty(taxesFees)) {
      // Reset error state when taxes are successfully fetched
      setHasError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasTaxesFetchError, hasUpdatedZip, taxesFees]);
  if (!enableTaxesAndFees && hasTaxesFetchError && !isUds) {
    return null;
  }
  if (!enableTaxesAndFees) {
    return null;
  }
  const isKbb = brand === 'kbb';
  const isOfferBreakdown = uiContext === 'offerBreakdown';
  const isSnapshot = uiContext === 'snapshot';
  const isWithRedesign = uiContext === 'withRedesign';
  const fontSize = isOfferBreakdown || isSnapshot || isWithRedesign || isOfferBreakdownModal ? 300 : 400;
  const handleTaxesFeesModalOpen = event => {
    const userModified = 'TAXES_AND_FEES_MODAL_OPENED';
    const eventData = {
      feature: ndsPencilingFeature,
      cmp: isKbb ? 'see_dtls' : 'tf_see_dtls',
      eventResult: 'see-details',
      userModified,
      inputName: 'taxesAndFee'
    };
    if (event?.target?.tagName?.toLowerCase() === 'button') {
      eventData.eventResult = 'taxes-fees';
      eventData.cmp = 'g_tax_fee';
    }
    onNdsInitialClick(event);
    handleNDSPencilling(event, eventData);
    setIsLoadingTaxes(true);
    handleNDSClick(event, {
      userModified,
      elementText: 'Get Taxes & Fees'
    });
    setShowTaxesFeesModal(true);
    setHasTaxesFeesModalOpen(true);
    fetchPaymentDetails(true, false, zip.toString());
  };
  const handleTaxesFeesModalHide = () => {
    setShowTaxesFeesModal(false);
    setHasTaxesFeesModalOpen(false);
  };
  const handleUpdatingTaxZip = function (updatedZip) {
    let apply = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    setIsLoadingTaxes(true);
    fetchPaymentDetails(true, apply, updatedZip.toString());
  };
  const handleSavingTaxPref = function (updatedZip, event) {
    let withAnalytics = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
    if (withAnalytics) {
      handleNDSPencilling(event, {
        cmp: isKbb ? 'ctn_btn' : 'tf_ctn_btn',
        eventResult: 'continue',
        feature: ndsPencilingFeature
      });
    }
    batch(() => {
      if (updatedZip) {
        updateUserPaymentInfo('hasRequestedTaxesFees', true, updatedZip);
      }
      if (!includeTaxesAndFees) {
        setIncludeTaxesAndFees(true);
      }
      setPaymentCalculationDetails({
        ...paymentCalculationDetails,
        apply: true
      });
      // This resets the customLender object so that the payment details, lending options modal,
      // and send offer lead form all have the same lender after adding taxes
      updatePaymentPencils({
        customLender: {}
      });
    });
  };
  const handleZipChange = event => {
    const updatedZip = event?.inputZip;
    if (updatedZip.length === 5) {
      setHasUpdatedZip(true);
      userZip.current = updatedZip;
      handleUpdatingTaxZip(updatedZip);
    }
  };
  const handleZipSubmit = event => {
    handleTaxesFeesModalHide();
    handleSavingTaxPref(hasUpdatedZip ? userZip.current : zip, event);
    setHasUpdatedZip(false);
  };
  const handleZipChangeAndSubmit = async formData => {
    const updatedZip = formData?.inputZip || formData;
    userZip.current = updatedZip;
    if (!updatedZip || updatedZip.length < 5) {
      setHasError(true);
      return;
    }

    // Reset error state when valid zip is entered
    setHasError(false);

    // analytics
    const userModified = 'OFFER_BREAKDOWN_MODAL_OPENED';
    const syntheticEvent = {
      target: {
        value: updatedZip
      }
    };
    handleNDSClick(syntheticEvent, userModified);
    handleNDSPencilling(syntheticEvent, {
      feature: ndsPencilingFeature,
      cmp: 'g_tax_fee',
      eventResult: 'taxes-fees',
      userModified,
      inputName: 'taxesAndFee'
    });
    if (isOfferBreakdownModal && taxesFees && Object.keys(taxesFees).length > 0 && updatedZip.toString() === prevFetchZip.current) {
      // if the zip hasn't changed, apply current taxes and fees without refetching
      handleSavingTaxPref(updatedZip.toString(), syntheticEvent, false);
    } else if (updatedZip && updatedZip.length === 5) {
      if (updatedZip !== prevFetchZip.current) {
        prevFetchZip.current = updatedZip; // track the zip used for this API call
        if (fetchIncentivesDetails) await fetchIncentivesDetails(updatedZip);
        await fetchPaymentDetails(true, true, updatedZip.toString());
        // Update the local state to reflect that taxes are now included
        setIncludeTaxesAndFees(true);
        updateUserPaymentInfo('hasRequestedTaxesFees', true, updatedZip);
      } else {
        // fallback, apply current taxes and fees without refetching
        handleSavingTaxPref(updatedZip.toString(), syntheticEvent, false);
      }
    }
    setIsLoadingTaxes(false);
  };
  const taxModalTrigger = showSeeDetails => tabName === activeTab && /*#__PURE__*/jsx(ModalTrigger, {
    showModal: showTaxesFeesModal,
    onClick: handleTaxesFeesModalOpen,
    modal: /*#__PURE__*/jsx(TaxesFeesModal, {
      activeTab: activeTab,
      container: container,
      showModal: showTaxesFeesModal,
      zip: zip,
      onHide: handleTaxesFeesModalHide,
      hasUpdatedZip: hasUpdatedZip,
      setHasUpdatedZip: setHasUpdatedZip,
      renderTaxesFeesBreakdown: renderTaxesFeesBreakdown,
      taxesFeesSectionProps: taxesFeesSectionProps,
      handleChange: handleZipChange,
      handleSubmit: handleZipSubmit
    }),
    children: TaxesAndFeesCTA({
      showSeeDetails,
      handleTaxesFeesModalOpen,
      isWithRedesign
    })
  });
  const taxesAndFeesText = /*#__PURE__*/jsxs(Text, {
    className: "display-flex flex-wrap gap-1",
    color: "subdued",
    size: fontSize,
    children: [isEstimate ? 'Est. Taxes & Fees' : 'Taxes & Fees', !isOfferBreakdown && /*#__PURE__*/jsx(InfoIcon, {
      id: `taxes-and-fees-info-${uiContext}`,
      infoText: "Usually includes sales tax, DMV fees, any lender fees for financing, plus dealer-related fees for doc and prep",
      placement: "top",
      className: isWithRedesign ? 'margin-right-2' : ''
    })]
  }, "taxesAndFeesText");
  let showTaxesAndFeesDetail = true;
  if (showInOfferBreakdownModal && !enableTaxesAndFees) {
    showTaxesAndFeesDetail = false;
  }
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsxs("div", {
      className: clsx('display-flex justify-content-between', {
        'margin-bottom-4': !isWithRedesign,
        'align-items-center': !(isOfferBreakdown || isWithRedesign),
        'align-items-top': isOfferBreakdown || isWithRedesign,
        'border-neutral-tertiary padding-3 rounded margin-bottom-1': isWithRedesign
      }),
      children: [/*#__PURE__*/jsxs("div", {
        className: clsx('display-block justify-content-between display-flex flex-wrap', {
          'gap-2 align-items-center': !isWithRedesign,
          'align-items-top': isWithRedesign
        }),
        children: [taxesAndFeesText, !isSnapshot && !isOfferBreakdownModal && includeTaxesAndFees && !isNationalRates && taxModalTrigger(true)]
      }), showTaxesAndFeesDetail && /*#__PURE__*/jsx(TaxesAndFeesDetail, {
        includeTaxesAndFees: includeTaxesAndFees,
        isLoading: isLoading,
        isNationalRates: isNationalRates,
        isSnapshot: isSnapshot,
        taxModalTrigger: taxModalTrigger,
        totalTaxesFees: totalTaxesFees,
        fontSize: fontSize,
        activeTab: activeTab,
        showInOfferBreakdownModal: showInOfferBreakdownModal,
        hasRequestedTaxesFees: hasRequestedTaxesFees,
        isUds: isUds,
        taxesFees: taxesFees,
        paymentErrorState: paymentErrorState,
        hasError: hasError
      })]
    }, "taxAndFee"), showInOfferBreakdownModal && enableTaxesAndFees && /*#__PURE__*/jsx(TaxesFeesBreakdownForm, {
      zip: zip,
      titleLabel: "Calculate Taxes & Fees",
      ctaLabel: "Get Taxes & Fees",
      onSubmit: handleZipChangeAndSubmit,
      renderTaxesFeesBreakdown: renderTaxesFeesBreakdown,
      taxesFeesSectionProps: taxesFeesSectionProps,
      isOfferBreakdownModal: showInOfferBreakdownModal
    })]
  });
}
export { TaxesAndFees as default };
